import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../config/supabaseClient';

const PasswordResetConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Session validation state
  const [isValidSession, setIsValidSession] = useState(false);
  const [validatingSession, setValidatingSession] = useState(true);

  // Get the type from URL params
  const type = searchParams.get('type');

  // Validate session on component mount
  useEffect(() => {
    const validateSession = async () => {
      try {
        setValidatingSession(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          navigate('/auth/login', { replace: true });
          return;
        }

        if (!session) {
          console.log('No session found, redirecting to login');
          navigate('/auth/login', { replace: true });
          return;
        }

        // Check if this is a recovery session
        if (type === 'recovery' || session.user.app_metadata?.provider === 'email') {
          setIsValidSession(true);
        } else {
          console.log('Invalid session type for password reset');
          navigate('/auth/login', { replace: true });
        }
      } catch (error) {
        console.error('Error validating session:', error);
        navigate('/auth/login', { replace: true });
      } finally {
        setValidatingSession(false);
      }
    };

    validateSession();
  }, [type, navigate]);

  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const redirectToLogin = () => {
    console.log('PasswordResetConfirm: Executing redirect to login...');
    // Multiple fallback methods to ensure redirect works
    try {
      // Method 1: React Router navigate
      navigate('/auth/login', { replace: true });
    } catch (navError) {
      console.error('Navigate failed:', navError);
    }
    
    // Method 2: Force page redirect (backup)
    setTimeout(() => {
      window.location.href = '/auth/login';
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      console.log('PasswordResetConfirm: Attempting to update password');
      
      const { data, error } = await supabase.auth.updateUser({
        password: formData.password
      });
      
      console.log('PasswordResetConfirm: Supabase response:', { data, error });
      
      if (error) {
        console.log('PasswordResetConfirm: Error updating password:', error);
        setError(error.message || 'Failed to update password');
        setLoading(false);
        return;
      }

      // Password updated successfully
      console.log('PasswordResetConfirm: Password updated successfully!');
      
      // Show success message and stop loading after 2 seconds
      setError('Password updated successfully! Click "Return to Login" below.');
      
      // Stop the loading spinner after 2 seconds
      setTimeout(() => {
        setLoading(false);
      }, 2000);
      
      // Sign out user in background (but don't wait for it)
      setTimeout(async () => {
        try {
          console.log('PasswordResetConfirm: Signing out user...');
          await supabase.auth.signOut();
          console.log('PasswordResetConfirm: User signed out successfully');
        } catch (signOutError) {
          console.error('Sign out error (continuing anyway):', signOutError);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Password update error:', error);
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Hard-coded redirect button for emergency fallback
  const handleForceRedirect = () => {
    console.log('PasswordResetConfirm: Force redirect button clicked');
    redirectToLogin();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Show loading while validating session
  if (validatingSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F8]">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Validating your session...</h2>
          <p className="text-gray-500 mt-2">Please wait while we verify your password reset link.</p>
        </div>
      </div>
    );
  }

  // Show main form
  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F8] px-4 pb-24">
      <div className="w-full max-w-md">
        {/* Header/Hero Section - Updated to match main page */}
        <header className="bg-primary shadow-sm mb-8 rounded-lg">
          <div className="px-6 py-8 text-center">
            <div className="flex flex-col items-center space-y-2">
              <div className="flex items-center gap-3">
                {/* Tennis ball icon */}
                <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  {/* Base circle with gradient */}
                  <circle cx="12" cy="12" r="11" fill="url(#tennis-gradient)" />
                  
                  {/* Tennis ball seam */}
                  <path
                    d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 2c4.97 0 9 4.03 9 9s-4.03 9-9 9-9-4.03-9-9 4.03-9 9-9z"
                    fill="#E5B210"
                    fillOpacity="0.6"
                  />
                  
                  {/* S-curve seam */}
                  <path
                    d="M7 6.5C9.5 9 14.5 7 17 4.5M7 17.5c2.5-2.5 7.5-0.5 10 2"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient id="tennis-gradient" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFB802" />
                      <stop offset="1" stopColor="#E5B210" />
                    </linearGradient>
                  </defs>
                </svg>
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Tennis<span className="text-accent">Now</span><span className="text-white">App.com</span>
                </h1>
              </div>
              <p className="text-white/80 text-sm md:text-base font-light">Connecting the Triangle's Tennis Community</p>
            </div>
          </div>
        </header>

        <section className="w-full max-w-md bg-white shadow-xl rounded-2xl p-10">
          <h2 className="text-2xl font-bold mb-2 text-center text-textDark">
            Set New Password
          </h2>
          
          <p className="text-center text-gray-600 mb-8">
            Enter your new password below.
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 
                           focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-textDark"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 
                           focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-textDark"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Only show the Update Password button if not loading or if there's an error */}
            {!loading && (
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primaryDark text-white font-semibold 
                         rounded-lg py-3 mt-8 transition-colors"
              >
                Update Password
              </button>
            )}

            {/* Show loading button for 2 seconds, then it disappears */}
            {loading && (
              <button
                type="button"
                disabled
                className="w-full bg-primary opacity-50 text-white font-semibold 
                         rounded-lg py-3 mt-8 cursor-not-allowed"
              >
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating Password...
                </div>
              </button>
            )}
            
            {/* Instructional message */}
            <div className="mt-4 text-center">
              <p className="text-gray-600 text-sm">
                Hit the return to login button below
              </p>
            </div>
            
            {/* Always show Return to Login button, more prominent when not loading */}
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={handleForceRedirect}
                className={`${
                  !loading 
                    ? 'bg-primary hover:bg-primaryDark text-white font-semibold rounded-lg py-3 px-6' 
                    : 'text-primary hover:text-primaryDark hover:underline text-sm'
                } transition-colors`}
              >
                Return to Login
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default PasswordResetConfirm; 